import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Loader2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { PlanDialogProps } from '../types';

const BASE_VARIANT = 'base';

export function PlanDialog({
  isDark,
  variants,
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  title,
  description,
  submitLabel,
  isSubmitting = false,
}: PlanDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        'max-w-4xl max-h-[90vh] overflow-y-auto',
        isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white'
      )}>
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#111111]')}>
            {title}
          </DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Код *</Label>
              <Input
                value={formData.code}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                placeholder="PREMIUM_1M"
                className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Название *</Label>
              <Input
                value={formData.title}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Premium 1 Month"
                className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Длительность *</Label>
              <Input
                type="number"
                value={formData.duration_value}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, duration_value: parseInt(e.target.value) || 1 }))}
                className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Единица *</Label>
              <Select
                value={formData.duration_unit}
                onValueChange={(value: 'week' | 'month' | 'year') => onFormDataChange(prev => ({ ...prev, duration_unit: value }))}
              >
                <SelectTrigger className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">Неделя</SelectItem>
                  <SelectItem value="month">Месяц</SelectItem>
                  <SelectItem value="year">Год</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Ценовой вариант</Label>
              <Select
                value={formData.variant_id ?? BASE_VARIANT}
                onValueChange={(value) =>
                  onFormDataChange(prev => ({
                    ...prev,
                    variant_id: value === BASE_VARIANT ? null : value,
                  }))
                }
              >
                <SelectTrigger className={cn(
                  isDark
                    ? 'bg-slate-800 border-slate-700 text-white'
                    : 'bg-white border-slate-200'
                )}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={BASE_VARIANT}>Базовый прайс-лист</SelectItem>
                  {variants
                    .filter(v => v.status !== 'archived')
                    .map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Валюта</Label>
              <Input
                value={formData.currency}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, currency: e.target.value }))}
                placeholder="UZS"
                className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Цена *</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, price: parseInt(e.target.value) || 0 }))}
                className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
            <div className="space-y-2">
              <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Старая цена</Label>
              <Input
                type="number"
                value={formData.old_price || ''}
                onChange={(e) => onFormDataChange(prev => ({ ...prev, old_price: e.target.value ? parseInt(e.target.value) : undefined }))}
                className={cn(
                  isDark 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-slate-200'
                )}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => onFormDataChange(prev => ({ ...prev, is_active: checked }))}
            />
            <Label className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>Активен</Label>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className={cn(
              isDark
                ? 'border-slate-700 text-slate-300 hover:bg-slate-800'
                : 'border-slate-200'
            )}
          >
            Отмена
          </Button>
          <Button onClick={onSubmit} disabled={isSubmitting} className="bg-[#C9A227] hover:bg-[#C9A227]/90">
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
